import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { GraduationCap, ShieldCheck, Users, School, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BrandWord } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { refreshSession, roleHome, redirectIfAuthed, type Role, type Grade, PRIMARY_GRADES, SECONDARY_GRADES } from "@/lib/session";

export const Route = createFileRoute("/auth")({
  ssr: false,
  beforeLoad: () => redirectIfAuthed(),
  head: () => ({
    meta: [
      { title: "Sign in — MAKAI" },
      { name: "description", content: "Create an account or sign in to MAKAI as a learner, parent, teacher or school admin." },
    ],
  }),
  component: AuthPage,
});

const ROLE_OPTIONS: { id: Exclude<Role, "admin">; title: string; desc: string; icon: typeof Users }[] = [
  { id: "learner", title: "Learner", desc: "Lessons, quizzes & Learning Tree", icon: GraduationCap },
  { id: "parent",  title: "Parent",  desc: "Follow your children's progress",   icon: ShieldCheck },
  { id: "teacher", title: "Teacher", desc: "Manage your classes & assignments", icon: Users },
];

type Mode = "signin" | "signup";

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("signin");
  const [role, setRole] = useState<Exclude<Role, "admin">>("learner");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [grade, setGrade] = useState<Grade>("P5");
  const [school, setSchool] = useState("");
  const [language, setLanguage] = useState("en");
  const [busy, setBusy] = useState(false);

  // Redirect away if already signed in
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session) return;
      const s = await refreshSession();
      if (s) navigate({ to: roleHome(s.role) });
    });
  }, [navigate]);

  async function handleSignIn() {
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    if (error) throw error;
    const s = await refreshSession();
    if (!s) throw new Error("Could not load your profile. Please contact support.");
    toast.success(`Welcome back, ${s.name}!`);
    navigate({ to: roleHome(s.role) });
  }

  async function handleSignUp() {
    if (password.length < 8) throw new Error("Password must be at least 8 characters.");
    if (!name.trim()) throw new Error("Please enter your full name.");
    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth`,
        data: {
          full_name: name.trim(),
          role,
          grade: role === "learner" ? grade : undefined,
          school_name: role !== "learner" ? school.trim() : undefined,
          phone: phone.trim() || undefined,
          preferred_language: language,
        },
      },
    });
    if (error) throw error;
    const s = await refreshSession();
    if (s) {
      toast.success(`Welcome to MAKAI, ${s.name}!`);
      navigate({ to: roleHome(s.role) });
    } else {
      toast.success("Check your email to confirm, then sign in.");
      setMode("signin");
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signin") await handleSignIn();
      else await handleSignUp();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  async function signInGoogle() {
    setBusy(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin + "/auth",
      });
      if (result.error) throw result.error;
      if (result.redirected) return;
      const s = await refreshSession();
      if (s) navigate({ to: roleHome(s.role) });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Google sign-in failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-gradient-hero p-12 text-primary-foreground lg:flex lg:flex-col lg:justify-between">
        <div className="pattern-kente absolute inset-0 opacity-20" aria-hidden />
        <BrandWord className="relative text-primary-foreground [&_span]:text-primary-foreground" />
        <div className="relative">
          <h2 className="font-display text-4xl font-bold leading-tight">
            Welcome back.<br />Your tree is waiting.
          </h2>
          <p className="mt-3 max-w-md text-primary-foreground/85">
            Sign in to continue lessons, see your Learning Tree grow and pick up where you left off.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-3">
            {["Numeracy","Literacy","Discovery","Expression"].map((b, i) => (
              <div key={b} className="rounded-xl bg-white/10 p-4">
                <div className="text-[11px] uppercase tracking-wider opacity-80">{b}</div>
                <div className="mt-1 font-display text-xl font-bold">{["68%","82%","45%","73%"][i]}</div>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-xs opacity-80">© MAKAI — built for Uganda.</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="mb-6 lg:hidden"><BrandWord /></div>

          <div className="mb-4 inline-flex rounded-full border border-border bg-card p-1 text-sm">
            <button
              type="button"
              onClick={() => setMode("signin")}
              className={cn("rounded-full px-4 py-1.5 font-medium transition", mode === "signin" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}
            >Sign in</button>
            <button
              type="button"
              onClick={() => setMode("signup")}
              className={cn("rounded-full px-4 py-1.5 font-medium transition", mode === "signup" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}
            >Create account</button>
          </div>

          <h1 className="font-display text-2xl font-bold sm:text-3xl">
            {mode === "signin" ? "Sign in to MAKAI" : "Create your MAKAI account"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {mode === "signin"
              ? "Welcome back — enter your email and password."
              : "Pick your role to set up the right dashboard."}
          </p>

          {mode === "signup" && (
            <div className="mt-6 grid grid-cols-3 gap-2">
              {ROLE_OPTIONS.map((r) => {
                const active = role === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setRole(r.id)}
                    className={cn(
                      "flex flex-col items-start gap-2 rounded-xl border p-3 text-left transition",
                      active ? "border-primary bg-primary-soft" : "border-border bg-card hover:border-primary/40",
                    )}
                  >
                    <div className={cn("grid h-8 w-8 place-items-center rounded-lg",
                      active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                      <r.icon className="h-4 w-4" />
                    </div>
                    <div className="text-xs font-semibold leading-tight">{r.title}</div>
                  </button>
                );
              })}
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "signup" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" placeholder="e.g. Amina Kintu" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="you@school.ug" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" placeholder={mode === "signup" ? "At least 8 characters" : "••••••••"} value={password} onChange={(e) => setPassword(e.target.value)} minLength={mode === "signup" ? 8 : 1} required />
            </div>

            {mode === "signup" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input id="phone" type="tel" placeholder="+256 7XX XXX XXX" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>

                {role === "learner" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="grade">Grade</Label>
                    <select
                      id="grade"
                      value={grade}
                      onChange={(e) => setGrade(e.target.value as Grade)}
                      className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <optgroup label="Primary">
                        {PRIMARY_GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                      </optgroup>
                      <optgroup label="Secondary">
                        {SECONDARY_GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                      </optgroup>
                    </select>
                  </div>
                )}

                {role !== "learner" && (
                  <div className="space-y-1.5">
                    <Label htmlFor="school">School name</Label>
                    <Input id="school" placeholder="e.g. Kampala Primary" value={school} onChange={(e) => setSchool(e.target.value)} />
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="lang">Preferred language</Label>
                  <select id="lang" value={language} onChange={(e) => setLanguage(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                    <option value="en">English</option>
                    <option value="lg">Luganda</option>
                    <option value="sw">Kiswahili</option>
                    <option value="rny">Runyankole</option>
                  </select>
                </div>
              </>
            )}

            <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90" size="lg">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <>{mode === "signin" ? "Sign in" : "Create account"} <ArrowRight className="ml-1 h-4 w-4" /></>
              )}
            </Button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
          </div>

          <Button type="button" variant="outline" onClick={signInGoogle} disabled={busy} className="w-full" size="lg">
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1A6.99 6.99 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.47 1.18 4.93l3.66-2.83z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            School Admin accounts are invite-only. Ask your school's existing admin to invite you.
          </p>
        </div>
      </div>
    </div>
  );
}
